import type { Identifier } from "ra-core";
import { useTranslate } from "ra-core";
import { Link } from "react-router";

import { ListEmptyState } from "../design/ListStates";
import { ProgressBar } from "../design/ProgressBar";
import { formatMxn } from "../incentives/IncentiveBars";
import type { GoalProgress } from "../incentives/incentiveProgress";

/**
 * Almikia Epic E7 (TASK-093): the Líder BPO dashboard's team-progress block
 * -- the team's own month against the team goal, one row per team member
 * against HER OWN goal (never sorted, ranked, badged or expressed as a share
 * of the team -- owner decision, Independence guide), and the "best BPO
 * team" bar (rule (h): the team's own quarterly units/MXN, never compared
 * with another team). Pure presentation -- `LiderDashboard.tsx` owns the
 * data fetching and the pure calculation (`incentiveProgress.ts`).
 */

type Translate = ReturnType<typeof useTranslate>;

export interface LiderTeamMemberRow {
  id: Identifier;
  name: string;
  goalProgress: GoalProgress;
}

export interface LiderBestBpoTeamModel {
  programName: string;
  periodLabel: string;
  teamUnits: number;
  teamAmountMxn: number;
}

export interface LiderTeamProgressProps {
  teamGoalProgress: GoalProgress;
  /** The team's raw units/MXN this month, shown even with no target set (DOMAIN RULE (g)). */
  teamUnits: number;
  teamAmountMxn: number;
  /** Alphabetical order (acceptance criteria) -- `LiderDashboard.tsx` sorts, this never re-sorts. */
  members: LiderTeamMemberRow[];
  /** `null` when `best_bpo_team` does not apply to the current quarter (DOMAIN RULE (j)). */
  bestBpoTeam: LiderBestBpoTeamModel | null;
  /**
   * The Metas screen (`/metas`, TASK-091) is desktop-only: it is registered
   * only in the desktop `CustomRoutes`, not the mobile shell's. Hide each
   * row's "Definir meta" link on mobile so it never lands on a not-found
   * page -- `LiderDashboard.tsx` passes its own `isMobile`.
   */
  showGoalActions: boolean;
}

const GoalProgressLine = ({
  goalProgress,
  translate,
}: {
  goalProgress: GoalProgress;
  translate: Translate;
}) => {
  if (goalProgress.status === "no_target") {
    return (
      <p className="text-base tabular-nums text-muted-foreground">
        {translate("crm.dashboard.lider.no_goal")}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {goalProgress.targetUnits != null ? (
        <>
          <p className="text-base tabular-nums">
            {goalProgress.actualUnits >= goalProgress.targetUnits
              ? translate("crm.dashboard.lider.goal_reached")
              : translate("crm.dashboard.lider.units_missing", {
                  smart_count:
                    goalProgress.targetUnits - goalProgress.actualUnits,
                })}
          </p>
          <ProgressBar
            value={goalProgress.actualUnits}
            max={Math.max(goalProgress.targetUnits, goalProgress.actualUnits)}
            target={goalProgress.targetUnits}
            label={translate("crm.dashboard.lider.units_bar_label")}
          />
        </>
      ) : null}
      {goalProgress.targetAmountMxn != null ? (
        <p className="text-base tabular-nums text-muted-foreground">
          {translate("crm.dashboard.lider.amount_progress", {
            actual: formatMxn(goalProgress.actualAmountMxn),
            target: formatMxn(goalProgress.targetAmountMxn),
          })}
        </p>
      ) : null}
    </div>
  );
};

const TeamGoalSection = ({
  teamGoalProgress,
  teamUnits,
  teamAmountMxn,
  translate,
}: {
  teamGoalProgress: GoalProgress;
  teamUnits: number;
  teamAmountMxn: number;
  translate: Translate;
}) => (
  <section className="flex flex-col gap-3 rounded-2xl border bg-card p-4">
    <h2 className="text-xl font-semibold text-muted-foreground">
      {translate("crm.dashboard.lider.team_goal_title")}
    </h2>
    {teamGoalProgress.status === "no_target" ? (
      <>
        <p className="text-base font-semibold">
          {translate("crm.dashboard.lider.no_goal")}
        </p>
        <p className="text-base tabular-nums text-muted-foreground">
          {translate("crm.dashboard.lider.units_and_amount", {
            units: teamUnits,
            amount: formatMxn(teamAmountMxn),
          })}
        </p>
      </>
    ) : (
      <GoalProgressLine goalProgress={teamGoalProgress} translate={translate} />
    )}
  </section>
);

const MemberRow = ({
  member,
  showGoalAction,
  translate,
}: {
  member: LiderTeamMemberRow;
  showGoalAction: boolean;
  translate: Translate;
}) => (
  <div className="flex flex-col gap-2 rounded-2xl bg-card p-4">
    <div className="flex items-center justify-between gap-3">
      <p className="text-base font-bold">{member.name}</p>
      {showGoalAction ? (
        <Link
          to="/metas"
          className="flex h-11 items-center rounded-xl px-3 text-sm font-semibold text-primary underline-offset-4 hover:underline"
        >
          {translate("crm.dashboard.lider.set_goal_action")}
        </Link>
      ) : null}
    </div>
    <GoalProgressLine
      goalProgress={member.goalProgress}
      translate={translate}
    />
  </div>
);

const BestBpoTeamCard = ({
  model,
  translate,
}: {
  model: LiderBestBpoTeamModel;
  translate: Translate;
}) => (
  <section className="flex flex-col gap-1 rounded-2xl border bg-card p-4">
    <h2 className="text-xl font-semibold text-muted-foreground">
      {model.programName}
    </h2>
    <p className="text-base text-muted-foreground">{model.periodLabel}</p>
    <p className="mt-1 text-2xl font-bold tabular-nums">
      {translate("crm.dashboard.lider.best_bpo_team_units_and_amount", {
        units: model.teamUnits,
        amount: formatMxn(model.teamAmountMxn),
      })}
    </p>
  </section>
);

export const LiderTeamProgress = ({
  teamGoalProgress,
  teamUnits,
  teamAmountMxn,
  members,
  bestBpoTeam,
  showGoalActions,
}: LiderTeamProgressProps) => {
  const translate = useTranslate();

  return (
    <div className="flex flex-col gap-6">
      <TeamGoalSection
        teamGoalProgress={teamGoalProgress}
        teamUnits={teamUnits}
        teamAmountMxn={teamAmountMxn}
        translate={translate}
      />

      <div className="flex flex-col gap-3">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.dashboard.lider.team_title")}
        </h2>
        {members.length === 0 ? (
          <ListEmptyState
            title={translate("crm.dashboard.lider.team_empty_title")}
            description={translate(
              "crm.dashboard.lider.team_empty_description",
            )}
          />
        ) : (
          <div className="flex flex-col gap-2">
            {members.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                showGoalAction={showGoalActions}
                translate={translate}
              />
            ))}
          </div>
        )}
      </div>

      {bestBpoTeam ? (
        <BestBpoTeamCard model={bestBpoTeam} translate={translate} />
      ) : null}
    </div>
  );
};
